import React, {useState, useEffect} from 'react';
import '../../App.css';
import edit from './../../images/edit.svg';
import closeButton from './../../images/close.svg';
import { lanoService } from "../../api/lanoService";

export default function Products() {
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [prev, setPrev] = useState(null);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [lanoToken, setLanoToken] = useState(null);
  const [error, setError] = useState(null);

  // Fetch Lano token and initial data
  const fetchLanoData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get Lano authentication token
      const token = await lanoService.getAuthToken();
      setLanoToken(token);
      
      // Get all tasks
      const tasksData = await lanoService.getAllTasks(token);
      
      // Extract the last page URL and fetch data from the last page
      if (tasksData && tasksData.links && tasksData.links.last) {
        loadUserData(tasksData.links.last, null, token);
      } else {
        setData(tasksData.data || []);
        setPrev(tasksData.links?.prev || null);
        if (tasksData.meta) {
          setCurrentPage(tasksData.meta.currentPage);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error initializing Lano data:', error);
      setError('Failed to load Lano data. Please try again later.');
      setLoading(false);
    }
  };

  const loadUserData = async (pageUrl, currentData, token) => {
    const authToken = token || lanoToken;
    if (!authToken) {
      console.error('No Lano token available');
      setError('Authentication token not available. Please refresh the page.');
      return;
    }

    setLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${authToken}`);

      const response = await fetch(pageUrl, {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      });
      
      const responseJson = await response.json();
      
      // Update the current page number only if the 'meta' property exists
      if (Object.prototype.hasOwnProperty.call(responseJson, "meta")) {
        setCurrentPage(responseJson.meta.currentPage);
      }
      
      setPrev(responseJson.links?.prev || null);
      // Update the data
      setData([...responseJson.data, ...(currentData || [])]);
    } catch (error) {
      console.error('Error loading user data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditClick = async (task) => {
    if (!lanoToken) {
      setError('Authentication token not available. Please refresh the page.');
      return;
    }

    try {
      const result = await lanoService.getTaskById(lanoToken, task.id);
      if (result && result.data) {
        setEditTaskId(task.id);
        setEditFormData({ ...result.data.attributes });
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      setError('Failed to fetch task details. Please try again.');
    }
  };

  const handleEditFormChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleUpdateTask = async () => {
    if (!lanoToken) {
      setError('Authentication token not available. Please refresh the page.');
      return;
    }

    try {
      const updatedTaskData = {
        title: editFormData.title,
        description: editFormData.description,
        starts_on: editFormData.startsOn,
        ends_on: editFormData.endsOn,
        assignee_id: editFormData.assigneeId,
        rate: editFormData.rate,
        state: editFormData.state,
        amount: editFormData.amount,
      };
      
      await lanoService.updateTask(lanoToken, editTaskId, updatedTaskData);
      
      // Close the popup
      setEditTaskId(null);
      // Refresh the task list
      fetchLanoData();
    } catch (error) {
      console.error('Error updating task:', error);
      setError('Failed to update task. Please try again.');
    }
  };

  const loadLastPage = () => {
    fetchLanoData();
  };

  const loadMore = () => {
    if (prev) {
      loadUserData(prev, data);
    }
  };
  
  useEffect(() => {
    // Fetch Lano data when component mounts
    fetchLanoData();
  }, []);

  // Total budget across the currently loaded tasks (budget = rate × units, computed by Lano)
  const totalBudget = data.reduce((sum, t) => sum + (parseFloat(t.attributes?.budget) || 0), 0);
  const formatEur = (n) => Number(n || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const taskItems = data.map((task) => (
    <div className='list-tasks-item' key={task.id}>
      <div style={{ flexBasis: '250px' }}>{task.attributes.title}</div>
      <div style={{ flexBasis: '300px' }}>{task.attributes.description}</div>
      <div style={{ flexBasis: '120px' }}>{task.attributes.startsOn}</div>
      <div style={{ flexBasis: '120px' }}>{task.attributes.endsOn}</div>
      <div style={{ flexBasis: '200px' }}>{task.attributes.state}</div>
      <div style={{ flexBasis: '200px' }}>{task.attributes.creatorEmail}</div>
      <div style={{ flexBasis: '100px' }}>{task.attributes.rate} {task.attributes.currency}</div>
      <div style={{ flexBasis: '120px' }}>{task.attributes.budget} {task.attributes.currency}</div>
      <div style={{ flexBasis: '80px' }}>{task.attributes.creatorId}</div>
      <button className='edit-button' onClick={() => handleEditClick(task)}>
        <img src={edit} alt="Edit" className="edit-container" />
      </button>
    </div>
  )).reverse();

  return (
    <>
      <div className="content-wrapper page">
        <div className='content-wrapp'>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '60px' }}>
              <div>
                <h1>All Tasks</h1>
                {!isLoading && (
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: '8px' }}>
                    Total budget ({data.length} loaded task{data.length === 1 ? '' : 's'}): {formatEur(totalBudget)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'end' }}>
                <button className='big-button' onClick={() => loadLastPage()}>Refresh</button>
                <a className='big-button' href='/admin/lanoids'>Lano IDs</a>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>
              {error}
            </div>
          )}

          <div className='content-wrapper content-wrapper-talent-list'>
            <div className='list-tasks-header'>
              <div style={{ flexBasis: '250px' }}>Title</div>
              <div style={{ flexBasis: '300px' }}>Description</div>
              <div style={{ flexBasis: '120px' }}>Start</div>
              <div style={{ flexBasis: '120px' }}>End</div>
              <div style={{ flexBasis: '200px' }}>State</div>
              <div style={{ flexBasis: '200px' }}>Email</div>
              <div style={{ flexBasis: '100px' }}>Rate</div>
              <div style={{ flexBasis: '120px' }}>Budget</div>
              <div style={{ flexBasis: '80px' }}>ID</div>
            </div>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>Loading tasks...</div>
            ) : (
              <div style={{ marginBottom: "30px" }}>
                {taskItems.length > 0 ? taskItems : <div style={{ textAlign: 'center', padding: '20px' }}>No tasks found</div>}
              </div>
            )}
          </div>
          <div className='content-wrapper'>
            {prev && !isLoading && (
              <button className='big-button' onClick={() => loadMore()}>Load More</button>
            )}
          </div>
        </div>

      {editTaskId && (
        <div className="edit-form-popup">
          <button className='edit-button' style={{float:'right', height:"20px"}} onClick={() => setEditTaskId(null)}>
            <img src={closeButton} alt="Close" className="edit-container" />
          </button>
          <div className="edit-form">
            <label>Title</label>
            <input type="text" name="title" value={editFormData.title || ''} onChange={handleEditFormChange} />
            <label>Description</label>
            <input type="text" name="description" value={editFormData.description || ''} onChange={handleEditFormChange} />
            <label>Starts On</label>
            <input type="text" name="startsOn" value={editFormData.startsOn || ''} onChange={handleEditFormChange} />
            <label>Ends On</label>
            <input type="text" name="endsOn" value={editFormData.endsOn || ''} onChange={handleEditFormChange} />
            <label>Rate</label>
            <input type="text" name="rate" value={editFormData.rate || ''} onChange={handleEditFormChange} />
            <label>Amount</label>
            <input type="text" name="amount" value={editFormData.amount || ''} onChange={handleEditFormChange} />
            <label>State</label>
            <input type="text" name="state" value={editFormData.state || ''} onChange={handleEditFormChange} />
            <label>Assignee ID</label>
            <input type="text" name="assigneeId" value={editFormData.assigneeId || ''} onChange={handleEditFormChange} />
            <button className='button' onClick={handleUpdateTask}>Update</button>
          </div>
        </div>
      )}
    </>
  );
}